if ( typeof CartForm !== 'function' ) {
	class CartForm extends HTMLElement {

		constructor(){
			super();
			this.ajaxifyCartItems();
		}

		ajaxifyCartItems(){

			this.form = this.querySelector('form');
			const prefix = window.KT_PREFIX || '';

			this.querySelectorAll('[data-js-cart-item]').forEach(item=>{

				const remove = item.querySelector(`.${prefix}remove`);
				if ( remove ) {
					remove.dataset.href = remove.getAttribute('href');
					remove.setAttribute('href', '');
					remove.addEventListener('click', (e)=>{
						e.preventDefault();
						this.updateCartQty(item, 0);
					})
				}

				const qty = item.querySelector(`.${prefix}qty`);
				if ( qty ) {
					qty.addEventListener('input', debounce(e=>{
						e.preventDefault();
						e.target.blur();
						this.updateCartQty(item, parseInt(qty.value));
					}, 500));
					qty.addEventListener('click', (e)=>{
						e.target.select();
					})
				}

			})

		}

		updateCartQty(item, newQty){

			const prefix = window.KT_PREFIX || '';
			const clickedIsGift = item.dataset.freeGift === 'true';
			// Only take the gift-aware path when the cart actually has gifts or paid
			// add-ons and the clicked line isn't a gift (gift rows are locked anyway).
			// Plain carts keep the fast default path — no extra request.
			const cartHasGifts = !!this.querySelector('[data-js-cart-item][data-free-gift="true"]');
			// Paid add-ons have no row of their own — they print as text under their
			// parent product — so detect them by that text element.
			const cartHasAddons = !!this.querySelector(`.${prefix}cart-item__addon`);

			if ( (cartHasGifts || cartHasAddons) && !clickedIsGift ) {
				const clickedKey = item.dataset.id;
				this.form.classList.add(`${prefix}processing`);
				// Read the authoritative cart (real line-item properties) rather than
				// trusting DOM attributes — robust across products and re-renders.
				fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
					.then(res => res.json())
					.then(cart => {
						const items = cart.items || [];
						const clicked = items.find(it => it.key === clickedKey);
						if ( !clicked ) { this._changeCartLine(item, newQty); return; }

						const clickedVariantId = String(clicked.variant_id);
						const has = v => v !== undefined && v !== null && v !== '';
						const isGift = it => {
							const p = it.properties || {};
							// Any of the gift markers any add-path can set.
							return p._upsell_is_gift === 'true' || p._free_gift === 'true'
								|| has(p._gift_with) || has(p._upsell_parent_variant_id)
								|| has(p._upsell_is_gift);
						};
						const parentOf = it => String((it.properties || {})._upsell_parent_variant_id || '');
						const bundleOf = it => String((it.properties || {})._bundle_id || '');
						const clickedBundleId = bundleOf(clicked);

						// Paid add-on lines bought with this product (matched on the parent
						// variant id stamped at add-to-cart). They're charged per unit of the
						// main product, so they scale with it — same as on the live site.
						const addons = items.filter(it => String((it.properties || {})._addon_parent_variant_id || '') === clickedVariantId);

						// Preferred: gifts that share this line's bundle id. The id is minted
						// per add-to-cart, so this line's gifts are matched exactly — even when
						// another mattress in the cart gifts the same product.
						if ( clickedBundleId ) {
							const bundleGifts = items.filter(it => isGift(it) && bundleOf(it) === clickedBundleId);
							if ( bundleGifts.length ) {
								return this._applyGiftScaling(items, clickedKey, newQty, bundleGifts, newQty, addons);
							}
						}

						// Legacy carts (no bundle id): link gifts by parent variant id…
						let children = items.filter(it => isGift(it) && !bundleOf(it) && parentOf(it) === clickedVariantId);
						// …fallback A: if this is the ONLY paying line, every gift belongs
						// to it (covers gifts that were added without a parent id).
						if ( !children.length ) {
							const paying = items.filter(it => !isGift(it));
							if ( paying.length === 1 && paying[0].key === clickedKey ) {
								children = items.filter(isGift);
							}
						}
						// …fallback B: multiple paying items — claim orphan gifts (no parent id)
						// that appear consecutively immediately after this line in the cart.
						// Shopify preserves add order, so gifts added right after their parent
						// sit next to it in the list.
						if ( !children.length ) {
							const clickedIdx = items.findIndex(it => it.key === clickedKey);
							if ( clickedIdx >= 0 ) {
								const orphans = [];
								for ( let i = clickedIdx + 1; i < items.length; i++ ) {
									const it = items[i];
									if ( !isGift(it) ) break; // stop at next paying item
									if ( !parentOf(it) ) orphans.push(it);
								}
								if ( orphans.length ) children = orphans;
							}
						}

						if ( !children.length && !addons.length ) { this._changeCartLine(item, newQty); return; }

						// Legacy: gifts follow the TOTAL quantity of their parent variant
						// across paying lines, since nothing ties them to a single line.
						let parentTotal = newQty;
						items.forEach(it => {
							if ( it.key !== clickedKey && !isGift(it) && String(it.variant_id) === clickedVariantId ) {
								parentTotal += it.quantity;
							}
						});
						return this._applyGiftScaling(items, clickedKey, newQty, children, parentTotal, addons);
					})
					.catch(() => { this._changeCartLine(item, newQty); });
				return;
			}

			// Plain cart / gift line → default single-line change.
			this._changeCartLine(item, newQty);
		}

		// One atomic /cart/update.js: the clicked line → newQty, its gifts →
		// mainQty × per-main (0 removes them), its paid add-ons → mainQty ×
		// per-main (0 removes them), every other line untouched. If the add-on
		// can't reach its scaled quantity (e.g. the service product is
		// stock-capped, which would make the WHOLE atomic update fail), retry
		// without scaling it so the shopper's quantity change always lands.
		_applyGiftScaling(items, clickedKey, newQty, children, mainQty, addons){
			const childKeys = new Set(children.map(c => c.key));
			const addonKeys = new Set((addons || []).map(a => a.key));
			const buildUpdates = (scaleAddons) => items.map(it => {
				if ( it.key === clickedKey ) return newQty;
				if ( childKeys.has(it.key) ) {
					const per = parseInt((it.properties || {})._upsell_per_main_qty, 10);
					if ( !isNaN(per) && per > 0 ) return mainQty * per;
					return mainQty <= 0 ? 0 : it.quantity;
				}
				if ( addonKeys.has(it.key) ) {
					if ( mainQty <= 0 ) return 0;
					if ( !scaleAddons ) return it.quantity;
					const per = parseInt((it.properties || {})._addon_per_main_qty, 10);
					return mainQty * ( !isNaN(per) && per > 0 ? per : 1 );
				}
				return it.quantity;
			});
			const post = (updates) => fetch(KROWN.settings.routes.cart_update_url, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify({ updates: updates })
			}).then(res => { if ( !res.ok ) { throw new Error('cart update rejected'); } });
			return post(buildUpdates(true))
				.catch(() => post(buildUpdates(false)).catch(() => {}))
				.then(() => this._rerenderCart());
		}

		// Re-render the cart section over AJAX (no full page reload) — same swap the
		// theme uses after a line change. Used after the atomic gift-scaling update.
		_rerenderCart(){
			const prefix = window.KT_PREFIX || '';
			const self = this;
			return fetch('?section_id=helper-cart')
				.then(response => response.text())
				.then(text => {
					const dom = new DOMParser().parseFromString(text, 'text/html');
					const cartFormInnerHTML = dom.getElementById('AjaxCartForm').innerHTML;
					const cartSubtotalEl = dom.getElementById('AjaxCartSubtotal');
					const cartItems = document.getElementById('AjaxCartForm');
					cartItems.innerHTML = cartFormInnerHTML;
					cartItems.ajaxifyCartItems();
					const countEl = cartItems.querySelector('[data-cart-count]');
					const count = countEl ? countEl.textContent : '0';
					document.querySelectorAll('[data-header-cart-count]').forEach(elm => {
						elm.textContent = count;
					});
					document.querySelectorAll('[data-header-cart-total]').forEach(elm => {
						const t = cartItems.querySelector('[data-cart-total]');
						if (t) elm.textContent = t.textContent;
					});
					self._updateHeaderBadge(count);
					const subEl = document.getElementById('AjaxCartSubtotal');
					if (subEl && cartSubtotalEl) subEl.innerHTML = cartSubtotalEl.innerHTML;
					self.dispatchEvent(new Event('cart-updated'));
				})
				.finally(() => { self.form.classList.remove(`${prefix}processing`); });
		}

		// Keep the header cart icon badge (.ukh__cartBadge) in sync after AJAX cart
		// changes. The badge is only rendered by Liquid at page load, so without this
		// it goes stale (e.g. shows "1" after the cart is emptied).
		_updateHeaderBadge(count){
			const n = parseInt(count, 10) || 0;
			document.querySelectorAll('.ukh__cart').forEach(cartLink => {
				let badge = cartLink.querySelector('.ukh__cartBadge');
				if (n > 0) {
					if (!badge) {
						badge = document.createElement('span');
						badge.className = 'ukh__cartBadge';
						cartLink.appendChild(badge);
					}
					badge.textContent = n;
					badge.style.display = '';
				} else if (badge) {
					badge.remove();
				}
			});
		}

		_changeCartLine(item, newQty){

			let alert = null;
			const prefix = window.KT_PREFIX || '';

			this.form.classList.add(`${prefix}processing`);
			if ( this.querySelector(`.${prefix}alert`) ) {
				this.querySelector(`.${prefix}alert`).remove();
			}

			// Target the line by its stable key (not line index) so removals that
			// shift line numbers (e.g. clearing gifts first) still hit the right line.
			const body = JSON.stringify({
				id: item.dataset.id,
				quantity: newQty
			});

			fetch(KROWN.settings.routes.cart_change_url, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
					body
				})
				.then(response => response.json())
				.then(response => {
					if ( response.status == 422 ) {
						// wrong stock logic alert
						alert = document.createElement('span');
						alert.classList.add(`${prefix}alert`, `${prefix}alert--error`);
						if ( typeof response.description === 'string' ) {
							alert.innerHTML = response.description;
						} else {
							alert.innerHTML = response.message;
						}
						KROWN.functions.eventDispatcher('krown:cart:error', {
							error: alert.textContent
						});
					}
					return fetch('?section_id=helper-cart');
				})
				.then(response => response.text())
				.then(text => {

					const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
					const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
					const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

					const cartItems = document.getElementById('AjaxCartForm');
					cartItems.innerHTML = cartFormInnerHTML;
					cartItems.ajaxifyCartItems();

					const _countEl = cartItems.querySelector('[data-cart-count]');
					const _count = _countEl ? _countEl.textContent : '0';
					document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
						elm.textContent = _count;
					});
					document.querySelectorAll('[data-header-cart-total]').forEach(elm=>{
						elm.textContent = cartItems.querySelector('[data-cart-total]').textContent;
					});
					this._updateHeaderBadge(_count);

					if ( alert !== null ) {
						this.form.prepend(alert);
					} 

					document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
							
					const event = new Event('cart-updated');
					this.dispatchEvent(event);

					const updatedItem = cartItems.querySelector(`[data-js-cart-item][data-line="${item.dataset.line}"]`);
					if ( updatedItem ) {
						KROWN.functions.eventDispatcher('krown:cart:update', {
							itemKey: updatedItem.dataset.id,
							itemQty: updatedItem.dataset.qty,
							itemVariantId: updatedItem.dataset.variant,
							itemProductId: updatedItem.dataset.productId
						});
					}

				})
				.catch(e => {
					console.log(e);
					let alert = document.createElement('span');
					alert.classList.add(`${prefix}alert`, `${prefix}alert--error`);
					alert.textContent = KROWN.settings.locales.cart_general_error;
					this.form.prepend(alert);
				})
				.finally(() => {
					this.form.classList.remove(`${prefix}processing`);
				});
		}

	} 


  if ( typeof customElements.get('cart-form') == 'undefined' ) {
		customElements.define('cart-form', CartForm);
	}

}

if ( typeof CartProductQuantity !== 'function' ) {

	class CartProductQuantity extends HTMLElement {
		constructor(){
			super();
			this._prefix = window.KT_PREFIX || '';
			this.querySelector(`.${this._prefix}qty-minus`).addEventListener('click', this.changeCartInput.bind(this));
			this.querySelector(`.${this._prefix}qty-plus`).addEventListener('click', this.changeCartInput.bind(this));
		}
		changeCartInput(){
			setTimeout(()=>{
				document.getElementById('AjaxCartForm').updateCartQty(this.closest('[data-js-cart-item]'), parseInt(this.querySelector(`.${this._prefix}qty`).value));
			}, 50);
		}
	}

  if ( typeof customElements.get('cart-product-quantity') == 'undefined' ) {
		customElements.define('cart-product-quantity', CartProductQuantity);
	}

}

// method for apps to tap into and refresh the cart

if ( ! window.refreshCart ) {

	window.refreshCart = (openDrawer = true) => {
		
		fetch('?section_id=helper-cart')
			.then(response => response.text())
			.then(text => {

			const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
			const cartFormInnerHTML = sectionInnerHTML.getElementById('AjaxCartForm').innerHTML;
			const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;

			const cartItems = document.getElementById('AjaxCartForm');
			cartItems.innerHTML = cartFormInnerHTML;
			cartItems.ajaxifyCartItems();

			document.querySelectorAll('[data-header-cart-count]').forEach(elm=>{
				elm.textContent = cartItems.querySelector('[data-cart-count]').textContent;
			});
			document.querySelectorAll('[data-header-cart-total]').forEach(elm=>{
				elm.textContent = cartItems.querySelector('[data-cart-total]').textContent;
			})
			
			document.getElementById('AjaxCartSubtotal').innerHTML = cartSubtotalInnerHTML;
			if ( document.querySelector('[data-js-site-cart-sidebar]') && openDrawer === true ) {
				document.querySelector('[data-js-site-cart-sidebar]').show();
			}

			if ( document.querySelector('cart-recommendations') ) {
				document.querySelector('cart-recommendations').innerHTML = '';
				document.querySelector('cart-recommendations').generateRecommendations();
			}

		})

	}

}

if ( ! customElements.get('cart-note') ) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce(event => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${KROWN.settings.routes.cart_update_url}`, { 
							method: 'POST',
							headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
							body 
						});
		  		}, 150)
				);
      }
    }
  );
}

if ( ! customElements.get('cart-discounts' ) ) {
	customElements.define(
		'cart-discounts',
		class CartDiscounts extends HTMLElement {

			constructor() {

				super();

				const button = this.querySelector('[data-js-discount-submit]');
				this.input = this.querySelector('[data-js-discount-input]');
				this.cartSubtotal = document.getElementById('AjaxCartSubtotal');
				this.cartDiscountError = this.querySelector('[data-js-discount-error]');
				this.discountCodes = document.querySelector('[data-js-discount-codes]');
				this._prefix = window.KT_PREFIX || '';
				this.loadDiscountButtons();

				button.addEventListener('click', (e) => {
					e.preventDefault();
					if ( this.input.value.length > 0 ) {

						this.cartSubtotal.classList.add(`${this._prefix}processing`);
						if (this.discountCodes) {
							this.discountCodes.classList.add(`${this._prefix}processing`);
						}

						const existingDiscounts = Array.from(this.querySelectorAll('[data-js-discount]')).map(elm => elm.dataset.jsDiscount);
						
						const allDiscounts = [...existingDiscounts, this.input.value];
						const discountString = allDiscounts.join(',');
						const body = JSON.stringify({ discount: discountString });

						fetch(`${KROWN.settings.routes.cart_update_url}`, { 
							method: 'POST',
							headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
							body 
						})
							.then(response => response.json())
							.then(response => {
								const discountCodes = response.discount_codes;
								const inputValue = this.input.value;
								const matchingCode = discountCodes.find(code => 
									code.code === inputValue
								);

								if (matchingCode) {
									if (matchingCode.applicable) {
										this.handleSuccess();
									} else {
										this.handleError();
									}
								} else {
									this.handleError();
								}
							})
							.catch(error => {
								this.handleError();
								console.log(`Error: ${error}`);
							})

					}

				});

			}

			handleSuccess(){
				fetch('?section_id=helper-cart')
					.then(response => response.text())
					.then(text => {
						const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
						const cartSubtotalInnerHTML = sectionInnerHTML.getElementById('AjaxCartSubtotal').innerHTML;
						this.cartSubtotal.innerHTML = cartSubtotalInnerHTML;
						this.cartSubtotal.classList.remove(`${this._prefix}processing`);
						this.cartDiscountError.innerHTML = '';
						if ( sectionInnerHTML.querySelector('[data-js-discount-codes]') ) {
							this.discountCodes.innerHTML = sectionInnerHTML.querySelector('[data-js-discount-codes]').innerHTML;
							this.discountCodes.classList.remove(`${this._prefix}processing`);
							this.loadDiscountButtons();
						}
						this.input.value = '';
					})
			}

			loadDiscountButtons(){
				this.querySelectorAll('[data-js-discount]').forEach(elm=>{
					if ( ! elm.classList.contains(`${this._prefix}enabled`) ) {
						elm.classList.add(`${this._prefix}enabled`);
						elm.addEventListener('click', (e)=>{
							e.preventDefault();
							this.cartSubtotal.classList.add(`${this._prefix}processing`);
							if (this.discountCodes) {
								this.discountCodes.classList.add(`${this._prefix}processing`);
							}
							const discountToRemove = elm.dataset.jsDiscount;
							
							const allDiscountElements = this.querySelectorAll('[data-js-discount]');
							const allDiscounts = Array.from(allDiscountElements).map(el => el.dataset.jsDiscount);
							
							const remainingDiscounts = allDiscounts.filter(discount => discount !== discountToRemove);
							
							const body = JSON.stringify({ 
								discount: remainingDiscounts.join(',') 
							});
							
							fetch(`${KROWN.settings.routes.cart_update_url}`, { 
								method: 'POST',
								headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
								body 
							})
								.then(() => {
									this.handleSuccess();
								})
								.catch(error => {
									console.error(`Error: ${error}`);
								});
						});
					}
				});
			}
			
			handleError(){
				this.cartSubtotal.classList.remove(`${this._prefix}processing`);
				if (this.discountCodes) {
					this.discountCodes.classList.remove(`${this._prefix}processing`);
				}
				this.cartDiscountError.innerHTML = window.KROWN.settings.locales.cart_discount_code_error;
			}

		}	
	)
}